// keyrd_console/src/components/NcptDiagnosisSelector.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { buildKeyrdHeaders } from "../api/headers";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

function DiagnosisChip({ code, label, onRemove }) {
  if (!code) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 6px",
        borderRadius: 9999,
        border: "1px solid #ddd",
        marginRight: 4,
        marginBottom: 4,
        backgroundColor: "#fafafa",
        fontSize: 12,
      }}
    >
      <span style={{ fontWeight: 500, marginRight: 4 }}>{code}</span>
      {label ? (
        <span style={{ marginRight: 6, color: "#555" }}>({label})</span>
      ) : null}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 12,
            lineHeight: 1,
          }}
          aria-label={`Remove ${code}`}
        >
          ×
        </button>
      )}
    </span>
  );
}

export default function NcptDiagnosisSelector({ apiBaseUrl, token, patientId }) {
  const [patientDiags, setPatientDiags] = useState([]);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientError, setPatientError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [lastQuery, setLastQuery] = useState("");

  const [addingCode, setAddingCode] = useState(null);

  const debounceRef = useRef(null);

  const hasAuth = Boolean(apiBaseUrl) && Boolean(token);
  const disabled = !hasAuth || !patientId;

  const baseUrl = apiBaseUrl
    ? apiBaseUrl.replace(/\/+$/, "")
    : "";

  // --------------------------------------------------
  // Load existing diagnoses for this patient
  // --------------------------------------------------
  const loadPatientDiagnoses = useCallback(async () => {
    if (!hasAuth || !patientId || !baseUrl) {
      setPatientDiags([]);
      setPatientError(null);
      return;
    }

    setPatientLoading(true);
    setPatientError(null);

    try {
      const resp = await fetch(
        `${baseUrl}/console/patients/${encodeURIComponent(patientId)}/diagnoses`,
        {
          headers: buildKeyrdHeaders({ token }),
        }
      );

      const data = await resp.json();
      if (!data.ok) {
        throw new Error(data.error || "Failed to load patient diagnoses");
      }
      setPatientDiags(data.diagnoses || []);
    } catch (err) {
      console.error("loadPatientDiagnoses error:", err);
      setPatientError("Could not load NCPT diagnoses for this patient.");
      setPatientDiags([]);
    } finally {
      setPatientLoading(false);
    }
  }, [baseUrl, patientId, token, hasAuth]);

  useEffect(() => {
    loadPatientDiagnoses();
  }, [loadPatientDiagnoses]);

  // --------------------------------------------------
  // Debounced search as the user types
  // --------------------------------------------------
  const runSearch = useCallback(
    async (term) => {
      const q = (term || "").trim();
      if (!hasAuth || !patientId || !baseUrl || q.length < MIN_CHARS) {
        setSearchLoading(false);
        return;
      }

      setSearchLoading(true);
      setSearchError(null);
      setLastQuery(q);

      try {
        const resp = await fetch(
          `${baseUrl}/console/ncpt/diagnoses?q=${encodeURIComponent(q)}`,
          {
            headers: buildKeyrdHeaders({ token }),
          }
        );
        const data = await resp.json();
        if (!data.ok) {
          throw new Error(data.error || "Search failed");
        }

        // ⬇️ Critical fix: use results / diagnoses instead of non-existent items
        const list = data.results || data.diagnoses || data.items || [];
        setSearchResults(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("NCPT search error:", err);
        setSearchResults([]);
        setSearchError("Could not search NCPT diagnoses.");
      } finally {
        setSearchLoading(false);
      }
    },
    [baseUrl, token, hasAuth, patientId]
  );

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmed = (value || "").trim();
    if (trimmed.length < MIN_CHARS) {
      setSearchResults([]);
      setSearchError(null);
      setLastQuery("");
      return;
    }

    debounceRef.current = setTimeout(() => {
      runSearch(trimmed);
    }, DEBOUNCE_MS);
  };

  const handleExplicitSearch = () => {
    const trimmed = (searchTerm || "").trim();
    if (trimmed.length < MIN_CHARS) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    runSearch(trimmed);
  };

  // --------------------------------------------------
  // Attach diagnosis when clicking a search result
  // --------------------------------------------------
  const handleAddDiagnosis = async (code) => {
    if (!code || disabled || !baseUrl) return;
    const clean = String(code).trim();
    if (!clean) return;

    setAddingCode(clean);
    try {
      const resp = await fetch(
        `${baseUrl}/console/patients/${encodeURIComponent(
          patientId
        )}/diagnoses`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...buildKeyrdHeaders({ token }),
          },
          body: JSON.stringify({ code: clean }),
        }
      );
      const data = await resp.json();
      if (!data.ok) {
        throw new Error(data.error || "Could not add diagnosis");
      }

      const diag = data.diagnosis;
      if (diag && diag.code) {
        const already = patientDiags.some((d) => d.code === diag.code);
        if (!already) {
          setPatientDiags((prev) => [...prev, diag]);
        }
      } else {
        await loadPatientDiagnoses();
      }
    } catch (err) {
      console.error("handleAddDiagnosis error:", err);
      setSearchError("Could not attach that NCPT diagnosis.");
    } finally {
      setAddingCode(null);
    }
  };

  // --------------------------------------------------
  // Remove chip
  // --------------------------------------------------
  const handleRemoveDiagnosis = async (code) => {
    if (!code || disabled || !baseUrl) return;
    const clean = String(code).trim();
    if (!clean) return;

    try {
      const resp = await fetch(
        `${baseUrl}/console/patients/${encodeURIComponent(
          patientId
        )}/diagnoses/${encodeURIComponent(clean)}`,
        {
          method: "DELETE",
          headers: buildKeyrdHeaders({ token }),
        }
      );

      let data = null;
      try {
        data = await resp.json();
      } catch {
        // tolerate non-JSON error bodies
      }

      if (!resp.ok || !data?.ok) {
        const msg = data?.error || `HTTP ${resp.status}`;
        throw new Error(msg);
      }

      setPatientDiags((prev) => prev.filter((d) => String(d.code) !== clean));
    } catch (err) {
      console.error("handleRemoveDiagnosis error:", err);
      setPatientError("Could not remove that NCPT diagnosis.");
    }
  };

  const trimmedTerm = (searchTerm || "").trim();
  const hasTypedEnough = trimmedTerm.length >= MIN_CHARS;

  const showNoResults =
    !searchLoading &&
    hasTypedEnough &&
    lastQuery &&
    lastQuery === trimmedTerm &&
    searchResults.length === 0 &&
    !searchError;

  return (
    <div
      style={{
        marginTop: 24,
        padding: 16,
        borderRadius: 8,
        border: "1px solid #e2e8f0",
        backgroundColor: "#ffffff",
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 14 }}>
        NCPT Diagnoses
      </h3>

      {/* Existing diagnoses as chips */}
      <div style={{ marginBottom: 8, minHeight: 24 }}>
        {patientLoading && <span>Loading patient diagnoses…</span>}
        {!patientLoading && patientDiags.length === 0 && (
          <span style={{ fontSize: 12, color: "#666" }}>
            No NCPT diagnoses attached yet.
          </span>
        )}
        {patientDiags.map((d) => (
          <DiagnosisChip
            key={d.id || d.code}
            code={d.code}
            label={d.label}
            onRemove={() => handleRemoveDiagnosis(d.code)}
          />
        ))}
      </div>

      {patientError && (
        <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 6 }}>
          {patientError}
        </div>
      )}

      {/* Search input + button */}
      <div style={{ display: "flex", marginBottom: 4 }}>
        <input
          type="text"
          disabled={disabled}
          value={searchTerm}
          onChange={handleChange}
          placeholder='Start typing (for example, "sodium", "energy")'
          style={{
            flex: 1,
            padding: "6px 8px",
            borderRadius: 4,
            border: "1px solid #cbd5e1",
            fontSize: 13,
          }}
        />
        <button
          type="button"
          disabled={disabled || !hasTypedEnough}
          onClick={handleExplicitSearch}
          style={{
            marginLeft: 8,
            padding: "6px 12px",
            borderRadius: 4,
            border: "1px solid #065f46",
            backgroundColor:
              disabled || !hasTypedEnough ? "#e5e7eb" : "#047857",
            color: disabled || !hasTypedEnough ? "#6b7280" : "#ffffff",
            fontSize: 13,
            cursor: disabled || !hasTypedEnough ? "not-allowed" : "pointer",
          }}
        >
          Search
        </button>
      </div>

      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>
        Type at least {MIN_CHARS} characters. Matching NCPT codes will appear
        below, click one to attach it to the patient.
      </div>

      {searchError && (
        <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 4 }}>
          {searchError}
        </div>
      )}

      {/* Stable-height results list */}
      <div
        style={{
          maxHeight: 220,
          minHeight: 40,
          overflowY: "auto",
          borderTop: "1px solid #e5e7eb",
          marginTop: 4,
          paddingTop: 4,
        }}
      >
        {searchLoading && (
          <div style={{ fontSize: 12, color: "#6b7280", padding: "4px 0" }}>
            Searching…
          </div>
        )}

        {showNoResults && (
          <div style={{ fontSize: 12, color: "#6b7280", padding: "4px 0" }}>
            No NCPT diagnoses found for "{trimmedTerm}".
          </div>
        )}

        {!searchLoading &&
          searchResults.map((item) => {
            const key = item.code || item.label;
            const isAdding = addingCode === item.code;
            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                onClick={() => handleAddDiagnosis(item.code)}
                onKeyDown={(evt) => {
                  if (evt.key === "Enter" || evt.key === " ") {
                    evt.preventDefault();
                    handleAddDiagnosis(item.code);
                  }
                }}
                style={{
                  padding: "6px 4px",
                  cursor: disabled ? "not-allowed" : "pointer",
                  borderRadius: 4,
                  marginBottom: 2,
                }}
              >
                <div style={{ fontSize: 13, marginBottom: 2 }}>
                  <span style={{ fontWeight: 600, marginRight: 6 }}>
                    {item.code}
                  </span>
                  <span>{item.label}</span>
                  {isAdding && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        color: "#6b7280",
                      }}
                    >
                      Attaching…
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>
                  {item.domain}
                  {item.class ? ` · ${item.class}` : ""}
                  {item.subclass ? ` · ${item.subclass}` : ""}
                </div>
              </div>
            );
          })}
      </div>

      {!hasAuth && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#9ca3af" }}>
          NCPT tools are disabled until an RDN token and base URL are present.
        </div>
      )}
      {hasAuth && !patientId && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#9ca3af" }}>
          Select a patient to attach NCPT diagnoses.
        </div>
      )}
    </div>
  );
}
